import { useCallback } from 'react';
import { useLocation, useNavigate, type Path } from 'react-router-dom';

import { CONTEXT_URL_PARAMS } from '@/features/utils';

type PreserveSearchOptions = {
  /**
   * When true, preserve only session-level params (see CONTEXT_URL_PARAMS)
   * from the current URL. Defaults to preserving the full search string.
   */
  sessionOnly?: boolean;
  /**
   * Explicit param overrides applied last; a null value removes the param
   * even when it would otherwise be preserved.
   */
  params?: Record<string, string | null>;
};

type NavigateWithSearchOptions = PreserveSearchOptions & {
  replace?: boolean;
  state?: unknown;
};

/**
 * Builds a navigation target from `to` (a pathname, optionally with its own
 * query string) that carries over params from `currentSearch`. Params in `to`
 * win over preserved ones.
 */
export const withPreservedSearch = (
  to: string,
  currentSearch: string,
  options?: PreserveSearchOptions
): Pick<Path, 'pathname' | 'search'> => {
  const queryIndex = to.indexOf('?');
  const pathname = queryIndex === -1 ? to : to.slice(0, queryIndex);
  const targetSearch = queryIndex === -1 ? '' : to.slice(queryIndex + 1);

  const current = new URLSearchParams(currentSearch);
  const merged = new URLSearchParams();
  if (options?.sessionOnly) {
    for (const key of CONTEXT_URL_PARAMS) {
      const value = current.get(key);
      if (value !== null) merged.set(key, value);
    }
  } else {
    current.forEach((value, key) => merged.set(key, value));
  }
  new URLSearchParams(targetSearch).forEach((value, key) =>
    merged.set(key, value)
  );
  for (const [key, value] of Object.entries(options?.params ?? {})) {
    if (value === null) {
      merged.delete(key);
    } else {
      merged.set(key, value);
    }
  }

  const serialized = merged.toString();
  return { pathname, search: serialized ? `?${serialized}` : '' };
};

export const useNavigateWithSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (to: string, options?: NavigateWithSearchOptions) => {
      const navigateOptions = options
        ? { replace: options.replace, state: options.state }
        : undefined;
      navigate(
        withPreservedSearch(to, location.search, options),
        navigateOptions
      );
    },
    [navigate, location.search]
  );
};
